/* ============================================================
   FORMULAS  (/formula)  — SESAAT Studio v3.9
   ============================================================
   Tema dan komponen mengikut app.cal.com (nilai diukur, bukan
   diagak). Susun atur: lebar penuh, grid 3 lajur, setiap kad
   membawa pratonton video contoh.

   Kad pertama sentiasa "Create your own formula" — ajakan,
   bukan sekadar tempat kosong.

   ⛔ Gating lesen dikira dalam config/formulaAccess.js — satu
   tempat sahaja, dikongsi dengan route pembuka /formula/:id.
   Jangan salin logik itu ke sini.

   ⚠️ Doc module dalam Firestore belum ada medan video contoh.
   `sampleVideoUrl` dibaca kalau wujud; kalau tiada, kad papar
   bingkai kosong — bukan video palsu.
   ============================================================ */

import { useMemo, useState } from 'react';
import { Search, Lock, MoreHorizontal, ArrowRight, Play, Plus, Clapperboard } from 'lucide-react';
import { getModule, getSceneCount } from '../config/modules';
import { computeFormulaAccess } from '../config/formulaAccess';
import { useT } from '../i18n';

import '../design/tokens.css';
import { Badge, Btn, IconBtn, Card, CardBody, CardFoot, PageHeader, Segmented, Field, EmptyState } from '../design/ui';

/* Penerangan sandaran bila doc module dalam Firestore tiada medan
   `description`. Nilai di sini ialah KUNCI kamus, bukan ayat — pemalar
   aras-modul tidak boleh memanggil useT(), jadi ayatnya dicari semasa
   render. ⛔ Kunci objek = id module (dipadan dengan Firestore) — jangan
   sentuh; hanya nilainya milik kamus. */
const FALLBACK_DESC = {
    'ai8saat': 'formula.desc.ai8saat',
    'ai16saat-half': 'formula.desc.ai16saat-half',
    'homeniche': 'formula.desc.homeniche',
    'goyang': 'formula.desc.goyang',
    'flownity': 'formula.desc.flownity',
    'smartcustom': 'formula.desc.smartcustom',
};

/* ⚠️ Ayat sebab-kunci dikira dalam config/formulaAccess.js — fail itu
   dikongsi dengan route pembuka /formula/:id, jadi ayatnya lahir dalam
   Bahasa Melayu di sana. Peta ini menukarnya kepada kunci kamus supaya
   kad berkunci turut bertukar bahasa. Kalau ayat di sana diubah, tambah
   barisnya di sini juga — sandaran memaparkan ayat asal, jadi kad tidak
   pernah keluar kosong. */
const LOCK_KEY = {
    'Formula ini belum dibuka untuk akaun anda.': 'formula.lock.noAccess',
    'Formula ini sedang diselenggara.': 'formula.lock.maintenance',
    'Percubaan percuma anda telah habis. Dapatkan add-on untuk teruskan.': 'formula.lock.trialUsed',
};

/* `t` dihantar sebagai parameter — ini fungsi biasa, bukan komponen,
   jadi ia tidak boleh memanggil useT() sendiri. */
function engineLabel(mod, t) {
    if (getModule(mod.id).promptSchema === 'manual') return t('formula.engine.manual');
    const n = getSceneCount(mod);
    return n === 1 ? t('formula.engine.scene.one') : t('formula.engine.scene.many', { n });
}

/* Pratonton video — bingkai menegak 9:16, sama bentuk dengan
   video TikTok yang formula ini hasilkan. */
function Preview({ mod }) {
    const url = mod.sampleVideoUrl || null;
    return (
        <div style={{
            height: 208, background: 'var(--s-ground)', borderBottom: '1px solid var(--s-line)',
            display: 'grid', placeItems: 'center', position: 'relative', overflow: 'hidden',
        }}>
            <div style={{
                width: 100, height: 178, borderRadius: 10, overflow: 'hidden',
                background: mod.color || '#2A2A2A',
                display: 'grid', placeItems: 'center',
                border: '1px solid rgba(255,255,255,.10)',
            }}>
                {url ? (
                    <video
                        src={url}
                        muted
                        loop
                        playsInline
                        preload="none"
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                ) : (
                    <Play size={22} strokeWidth={1.6} style={{ color: 'rgba(255,255,255,.55)' }} />
                )}
            </div>

            <span style={{
                position: 'absolute', bottom: 10, right: 12,
                fontSize: 11, color: 'var(--s-ink-3)',
            }}>
                9:16
            </span>
        </div>
    );
}

function CreateCard({ onStart }) {
    const t = useT();
    return (
        <Card
            hover
            style={{ borderStyle: 'dashed', borderColor: 'var(--s-line-strong)', display: 'flex', flexDirection: 'column' }}
        >
            <div style={{
                height: 208, display: 'grid', placeItems: 'center',
                borderBottom: '1px dashed var(--s-line-strong)',
            }}>
                <span style={{
                    width: 44, height: 44, borderRadius: 12, display: 'grid', placeItems: 'center',
                    background: 'var(--s-fill)', color: 'var(--s-ink)',
                }}>
                    <Plus size={20} strokeWidth={2} />
                </span>
            </div>

            <CardBody style={{ flex: 1 }}>
                <h2 className="s-cardname" style={{ marginBottom: 6 }}>{t('formula.create.title')}</h2>
                <p className="s-meta">{t('formula.create.body')}</p>
            </CardBody>

            <CardFoot>
                <span className="s-dim">{t('formula.create.private')}</span>
                <Btn size="sm" onClick={onStart} icon={<ArrowRight size={14} strokeWidth={2} />}>
                    {t('formula.create.cta')}
                </Btn>
            </CardFoot>
        </Card>
    );
}

function FormulaCard({ mod, isActive, isLocked, lockReason, onOpen }) {
    const t = useT();
    const kunciDesc = FALLBACK_DESC[mod.id];
    /* mod.description datang dari Firestore (ditulis admin) — ia kandungan,
       bukan antara muka, jadi ia TIDAK diterjemah. */
    const desc = mod.description || t(kunciDesc || 'formula.desc.fallback');
    const sebabKunci = LOCK_KEY[lockReason] ? t(LOCK_KEY[lockReason]) : lockReason;
    const badge = getModule(mod.id).badge;

    return (
        <Card hover={!isLocked} style={{ display: 'flex', flexDirection: 'column', opacity: isLocked ? 0.55 : 1 }}>
            <Preview mod={mod} />

            <CardBody style={{ flex: 1 }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 7, flexWrap: 'wrap', marginBottom: 5 }}>
                            <h2 className="s-cardname">{mod.name}</h2>
                            {isActive && <Badge tone="brand">{t('formula.badge.active')}</Badge>}
                            {badge && <Badge tone="neutral">{badge}</Badge>}
                            {isLocked && <Badge tone="neutral"><Lock size={11} strokeWidth={2} />{t('formula.badge.locked')}</Badge>}
                        </div>

                        <p className="s-meta" style={{ marginBottom: 10 }}>{isLocked ? sebabKunci : desc}</p>

                        <div style={{ display: 'flex', alignItems: 'center', gap: 7, color: 'var(--s-ink-2)' }}>
                            <Clapperboard size={14} strokeWidth={1.9} />
                            <span className="s-meta">{t('formula.card.meta', { engine: engineLabel(mod, t) })}</span>
                        </div>
                    </div>

                    <IconBtn label={t('formula.card.options', { name: mod.name })} icon={<MoreHorizontal size={16} strokeWidth={2} />} />
                </div>
            </CardBody>

            <CardFoot>
                <span className="s-dim">
                    {isLocked ? t('formula.card.upgrade') : t('formula.card.ready')}
                </span>
                {!isLocked && (
                    <Btn size="sm" variant="ghost" icon={<ArrowRight size={14} strokeWidth={2} />} onClick={onOpen}>
                        {t('formula.card.open')}
                    </Btn>
                )}
            </CardFoot>
        </Card>
    );
}

/* ---------- Formula pelanggan (custom) --------------------------------
   ⭐ DUA JENIS DI RAK (keputusan user 2026-08-09):
        Engine  — produk rasmi SESAAT (robot + master prompt SESAAT).
                  Tidak boleh diedit, tidak boleh disalin.
        Formula — master prompt yang pelanggan tulis sendiri, berjalan
                  atas robot yang sama.

   ⚠️ PROTOTAIP: kad di bawah data CONTOH dalam fail ini. Halaman sebenar
      membaca `users/{uid}/formulas/*`. Lihat PLAN_FORMULA_SENDIRI.md.

   `namaKunci`/`descKunci` hanya wujud pada data contoh ini supaya kad
   prototaip turut bertukar bahasa. Formula SEBENAR pelanggan membawa
   `name`/`description` yang dia tulis sendiri — itu kandungan, dan kad
   memaparkannya sebagaimana adanya.  */
const FORMULA_CONTOH = [
    {
        id: 'f1', namaKunci: 'formula.mine.sample.f1.name', scene: 1, setCount: 3, status: 'live',
        descKunci: 'formula.mine.sample.f1.desc',
        mode: 'single',
    },
    {
        id: 'f2', namaKunci: 'formula.mine.sample.f2.name', scene: 2, setCount: 3, status: 'draft',
        descKunci: 'formula.mine.sample.f2.desc',
        mode: 'separate',
    },
];

function MyFormulaCard({ f, onEdit, onUse }) {
    const t = useT();
    const draf = f.status !== 'live';
    const nama = f.namaKunci ? t(f.namaKunci) : f.name;
    const huraian = f.descKunci ? t(f.descKunci) : f.description;
    return (
        <Card hover style={{ display: 'flex', flexDirection: 'column' }}>
            <Preview mod={f} />

            <CardBody style={{ flex: 1 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 7, flexWrap: 'wrap', marginBottom: 5 }}>
                    <h2 className="s-cardname">{nama}</h2>
                    {/* Draf = belum lulus ujian, jadi ia BELUM boleh dipakai Generate.
                        Ditanda di sini supaya pelanggan tidak tertanya kenapa formulanya
                        tiada dalam pemilih Workspace. */}
                    {draf && <Badge tone="warn">{t('formula.mine.badge.draft')}</Badge>}
                </div>

                <p className="s-meta" style={{ marginBottom: 10 }}>{huraian}</p>

                <div style={{ display: 'flex', alignItems: 'center', gap: 7, color: 'var(--s-ink-2)' }}>
                    <Clapperboard size={14} strokeWidth={1.9} />
                    <span className="s-meta">
                        {t('formula.mine.meta', {
                            scene: f.scene,
                            set: f.setCount,
                            mode: t(f.mode === 'single' ? 'formula.mine.mode.single' : 'formula.mine.mode.separate'),
                        })}
                    </span>
                </div>
            </CardBody>

            <CardFoot>
                <button
                    type="button"
                    onClick={onEdit}
                    style={{
                        padding: 0, border: 0, background: 'none', cursor: 'pointer',
                        fontFamily: 'inherit', fontSize: 12.5, color: 'var(--s-ink-3)',
                    }}
                >
                    {t('formula.mine.edit')}
                </button>
                <Btn
                    size="sm"
                    variant={draf ? 'ghost' : 'primary'}
                    disabled={draf}
                    onClick={onUse}
                    title={draf ? t('formula.mine.tip.draft') : t('formula.mine.tip.use')}
                    icon={<ArrowRight size={14} strokeWidth={2} />}
                >
                    {draf ? t('formula.mine.untested') : t('formula.mine.use')}
                </Btn>
            </CardFoot>
        </Card>
    );
}

export default function FormulaShelfView({
    modules = [],
    activeModuleId,
    isAdmin = false,
    userAllowedModules = [],
    userTier,
    flownityTrialUsed = 0,
    maintenanceLockedModules = [],
    onOpenFormula,
    onNavigate,
}) {
    const t = useT();
    const [scope, setScope] = useState('official');
    const [cari, setCari] = useState('');

    const senarai = useMemo(() => {
        return modules
            .map((mod) => {
                const acc = computeFormulaAccess({
                    mod, isAdmin, userAllowedModules, userTier,
                    flownityTrialUsed, maintenanceLockedModules,
                });
                if (acc.hidden) return null;
                return { mod, ...acc };
            })
            .filter(Boolean);
    }, [modules, isAdmin, userAllowedModules, userTier, flownityTrialUsed, maintenanceLockedModules]);

    const ditapis = useMemo(() => {
        const q = cari.trim().toLowerCase();
        if (!q) return senarai;
        return senarai.filter((r) =>
            (r.mod.name || '').toLowerCase().includes(q) || (r.mod.id || '').toLowerCase().includes(q));
    }, [senarai, cari]);

    const handleOpen = (row) => {
        if (row.isLocked) {
            if (row.isFlownityTrialCandidate && !row.mod.locked && flownityTrialUsed >= 5) onNavigate?.('/pricing');
            return;
        }
        onOpenFormula?.(row.mod.id);
    };

    /* Editor formula (/formula/editor). Dahulunya butang ini menghala ke
       /pro-studio kerana editor belum wujud — Pro Studio ialah tempat
       tetapan gaya disimpan, BUKAN tempat formula dibina, jadi pengguna
       mendarat di halaman yang tidak menjawab apa yang butang ini janjikan. */
    const startBuilding = () => onNavigate?.('/formula/editor');

    return (
        <div className="sesaat-m-tight" style={{ padding: '26px 28px 48px' }}>

            <PageHeader
                title={t('formula.page.title')}
                subtitle={t('formula.page.subtitle')}
                actions={
                    <>
                        {/* "Engine" bukan "Official" (kosa kata user 2026-08-09) — rak
                            memang ada DUA jenis, dan nama yang berbeza itulah yang
                            memberitahu pelanggan yang satu tak boleh diedit dan satu
                            lagi miliknya. */}
                        <Segmented
                            value={scope}
                            onChange={setScope}
                            options={[
                                { value: 'official', label: t('formula.scope.engine') },
                                { value: 'mine', label: t('formula.scope.mine') },
                                { value: 'community', label: t('formula.scope.community'), disabled: true },
                            ]}
                        />
                        <Btn onClick={startBuilding} icon={<Plus size={15} strokeWidth={2} />}>{t('formula.action.new')}</Btn>
                    </>
                }
            />

            <div style={{ marginBottom: 18 }}>
                <Field
                    icon={<Search size={15} strokeWidth={1.9} />}
                    placeholder={t('formula.search.placeholder')}
                    value={cari}
                    onChange={(e) => setCari(e.target.value)}
                    style={{ maxWidth: 320 }}
                />
            </div>

            {scope === 'mine' ? (
                <>
                    <p className="s-meta" style={{ marginBottom: 14 }}>
                        {t('formula.mine.intro')}{' '}
                        <span className="s-dim">{t('formula.mine.prototypeNote')}</span>
                    </p>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 14 }}>
                        <CreateCard onStart={startBuilding} />
                        {FORMULA_CONTOH.map((f) => (
                            <MyFormulaCard
                                key={f.id}
                                f={f}
                                onEdit={startBuilding}
                                onUse={() => onNavigate?.('/workspace-v3.9')}
                            />
                        ))}
                    </div>
                </>
            ) : modules.length === 0 ? (
                <EmptyState title={t('formula.empty.loading.title')}>{t('formula.empty.loading.body')}</EmptyState>
            ) : (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 14 }}>
                    <CreateCard onStart={startBuilding} />

                    {ditapis.map((row) => (
                        <FormulaCard
                            key={row.mod.id}
                            mod={row.mod}
                            isActive={row.mod.id === activeModuleId}
                            isLocked={row.isLocked}
                            lockReason={row.lockReason}
                            onOpen={() => handleOpen(row)}
                        />
                    ))}

                    {ditapis.length === 0 && (
                        <div style={{ gridColumn: '2 / -1' }}>
                            <EmptyState title={t('formula.empty.noMatch.title')}>
                                {t('formula.empty.noMatch.body')}
                            </EmptyState>
                        </div>
                    )}
                </div>
            )}

        </div>
    );
}
